import type { ComputedRef, InjectionKey, Ref } from 'vue'
import { computed, inject, onScopeDispose, provide, shallowReactive, shallowRef, watch } from 'vue'

interface DrawerGroupLayer {
  show: Ref<boolean>
  width: Ref<number>
}

interface DrawerGroup {
  target: string | Ref<HTMLElement | null>
  register: (
    show: Ref<boolean>,
    width: Ref<number>,
  ) => {
    zIndex: ComputedRef<number>
    afterLeave: () => void
  }
}

const drawerGroupKey: InjectionKey<DrawerGroup> = Symbol('drawer-group')

export function provideDrawerGroup(target: DrawerGroup['target']) {
  const layers = shallowReactive(new Map<symbol, DrawerGroupLayer>())
  const opened = shallowRef<symbol[]>([])
  const width = computed(() => {
    const active = opened.value.findLast(id => layers.get(id)?.show.value)
    return active ? layers.get(active)!.width.value : 0
  })

  provide(drawerGroupKey, {
    target,
    register(show, width) {
      const id = Symbol('drawer-layer')
      layers.set(id, { show, width })

      function remove() {
        opened.value = opened.value.filter(layer => layer !== id)
      }

      watch(
        show,
        value => {
          if (value) {
            // Opening order also covers reopening a layer while it is leaving.
            opened.value = [...opened.value.filter(layer => layer !== id), id]
          }
        },
        { immediate: true, flush: 'sync' },
      )

      onScopeDispose(() => {
        remove()
        layers.delete(id)
      })

      return {
        zIndex: computed(() => Math.max(0, opened.value.indexOf(id))),
        afterLeave() {
          if (!show.value) {
            remove()
          }
        },
      }
    },
  })

  return { width }
}

export function useDrawerGroup() {
  return inject(drawerGroupKey, undefined)
}
